import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, RefreshCw, AlertTriangle, Building2, PlayCircle, PauseCircle, CalendarClock, FileCheck2, FileWarning } from 'lucide-react';
import api from '../../api';

const Skeleton = ({ w = 'col-6', h = 20 }) => (
  <span className={`placeholder d-block rounded ${w}`} style={{ height: h }} />
);

const SummaryCard = ({ label, value, icon: Icon, color, bg, loading }) => (
  <div className="col-6 col-md-4 col-xl-2">
    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `3px solid ${color}` }}>
      <div className="card-body d-flex align-items-center gap-3 py-3">
        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 42, height: 42, background: bg }}>
          <Icon size={19} color={color} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-muted text-truncate" style={{ fontSize: '0.7rem' }}>{label}</div>
          {loading
            ? <div className="placeholder-glow mt-1"><Skeleton w="col-6" h={22} /></div>
            : <div className="fw-bold mt-1" style={{ fontSize: '1.25rem', lineHeight: 1, color }}>{(value ?? 0).toLocaleString('id-ID')}</div>}
        </div>
      </div>
    </div>
  </div>
);

const LIFECYCLE_BADGE = {
  ONGOING: { bg: '#e7f6ec', color: '#16a34a' },
  UPCOMING: { bg: '#fff7e6', color: '#d97706' },
  NOT_STARTED: { bg: '#f1f3f5', color: '#495057' },
  COMPLETED: { bg: '#e7f1fb', color: '#2563eb' },
};

// Sales-facing view of their own clients (Audit Platform -> Sales Client Dashboard):
// which clients are ongoing / not started / upcoming, and which still need a contract.
// Scoped server-side to the current Sales user's own assigned clients by default —
// never silently widened to everyone (see backend ClientMonitoringController).
const SalesClientDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/crm/sales/client-monitoring/dashboard');
      setData(res.data);
    } catch (err) {
      setError('Gagal memuat Sales Client Dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const clients = data?.clients || [];

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-2 d-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34, background: 'rgba(31,101,108,0.10)' }}>
              <LayoutDashboard size={18} color="#1f656c" />
            </div>
            <h1 className="h4 mb-0 fw-semibold" style={{ color: '#1a2e35' }}>Sales Client Dashboard</h1>
          </div>
          <div className="text-muted small mt-1">
            Client Anda: mana yang sedang berjalan, belum mulai, akan mulai, dan mana yang butuh kontrak.
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary d-flex align-items-center gap-1" onClick={load}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3">
          <AlertTriangle size={16} /> <span className="small">{error}</span>
        </div>
      )}

      <div className="row g-3 mb-3">
        <SummaryCard label="Total Client" value={summary.totalClients} icon={Building2} color="#1f656c" bg="rgba(31,101,108,0.10)" loading={loading} />
        <SummaryCard label="Sedang Berjalan" value={summary.ongoingClients} icon={PlayCircle} color="#16a34a" bg="rgba(22,163,74,0.10)" loading={loading} />
        <SummaryCard label="Belum Berjalan" value={summary.notStartedClients} icon={PauseCircle} color="#868e96" bg="rgba(134,142,150,0.10)" loading={loading} />
        <SummaryCard label="Akan Berjalan" value={summary.upcomingClients} icon={CalendarClock} color="#d97706" bg="rgba(217,119,6,0.10)" loading={loading} />
        <SummaryCard label="Punya Kontrak" value={summary.clientsWithContract} icon={FileCheck2} color="#2563eb" bg="rgba(37,99,235,0.10)" loading={loading} />
        <SummaryCard label="Butuh Kontrak" value={summary.clientsNeedingContract} icon={FileWarning} color="#dc2626" bg="rgba(220,38,38,0.10)" loading={loading} />
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="fw-semibold mb-3" style={{ color: '#1a2e35' }}>Daftar Client</div>
          {loading ? (
            <div className="d-flex flex-column gap-2">
              {[1, 2, 3].map((i) => <div key={i} className="placeholder-glow"><Skeleton w="col-12" h={36} /></div>)}
            </div>
          ) : clients.length === 0 ? (
            <div className="text-muted small text-center py-4">Belum ada client yang ter-assign ke Anda.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-vcenter card-table table-hover">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Status</th>
                    <th className="text-center">Jumlah Project</th>
                    <th>Project Berikutnya</th>
                    <th>Kontrak</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.companyId} role="button" style={{ cursor: 'pointer' }} onClick={() => navigate(`/clients/${c.companyId}`)}>
                      <td className="fw-medium">{c.companyName}</td>
                      <td>
                        <span className="badge rounded-pill" style={{
                          background: (LIFECYCLE_BADGE[c.lifecycleStatus] || LIFECYCLE_BADGE.NOT_STARTED).bg,
                          color: (LIFECYCLE_BADGE[c.lifecycleStatus] || LIFECYCLE_BADGE.NOT_STARTED).color,
                          fontWeight: 600, fontSize: '0.72rem', padding: '5px 10px',
                        }}>
                          {c.lifecycleStatusLabel}
                        </span>
                      </td>
                      <td className="text-center">{c.projectCount ?? 0}</td>
                      <td>{c.nextProjectStartDate || '-'}</td>
                      <td>
                        {c.hasContract
                          ? <span className="badge bg-green-lt text-green">Ada Kontrak{c.contractStatus ? `: ${c.contractStatus}` : ''}</span>
                          : <span className="badge bg-orange-lt text-orange">Butuh Kontrak</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesClientDashboard;
